import { BicHelper, Calculation, Dates, ReportType, CumulativeCalculation } from "@salaxy/core";
import { CalculationMapper } from "../calculation/CalculationMapper";
import { ESalaryReport } from "./ESalaryReport";
import { IESalaryCumulative } from "./IESalaryCumulative";
import { CalculationReport } from "../../mappers";

/**
 * Mapper for ESalary
 */
export class ESalaryMapper {

  /**
   * Maps input calc to hbs supported format
   * @param businessObject calculation to be mapped
   * @returns Extended calculation with eSalary property
   */
  public static getESalary(businessObject: Calculation): ESalaryReport {
    const eSalary: ESalaryReport = businessObject;
    eSalary.eSalaryHbs = {};
    eSalary.eSalaryHbs.version = "2.0";
    eSalary.eSalaryHbs.headerData = {
      messageId: businessObject.id,
      timestamp: Dates.getToday(),
      documentTitle: "SALAXY.REPORTS.eSalary.headerData.documentTitle",
      payDate: Dates.format(businessObject.workflow.salaryDate ?? businessObject.workflow.salaryPaidAt ?? businessObject.workflow.paidAt ?? Dates.getDate("today", "today", "today"), "YYYYMMDD"),
      netPayment: {
        label: "SALAXY.REPORTS.eSalary.headerData.netPayment.label",
        value: businessObject.result.workerCalc.totalWorkerPayment.toString()
      },
      payer: businessObject.employer.avatar.displayName,
      partyIdentifications: [
        { authority: "Y-tunnus", value: businessObject.employer.officialId },
      ],
      deliveryChannels: [
        {
          deliveryMethodCode: "01",
          deliveryInfo: {
            bankCode: businessObject.worker?.paymentData?.ibanNumber?.slice(4, 10) ?? ""
          }
        }
      ]
    };
    eSalary.eSalaryHbs.payPeriod = {
      heading: "SALAXY.REPORTS.eSalary.payPeriod.heading",
      dateOfPayment: {
        label: "SALAXY.REPORTS.eSalary.payPeriod.dateOfPayment.label",
        date: businessObject.workflow.salaryDate ?? businessObject.workflow.salaryPaidAt ?? businessObject.workflow.paidAt ?? Dates.getDate("today", "today", "today"),
      },
      periodDates: {
        label: "SALAXY.REPORTS.eSalary.payPeriod.periodDates.label",
        startDate: {
          label: "SALAXY.REPORTS.eSalary.payPeriod.periodDates.startDate.label",
          date: businessObject.worktime?.workDays?.start ?? null
        },
        termDate: {
          label: "SALAXY.REPORTS.eSalary.payPeriod.periodDates.termDate.label",
          date: businessObject.worktime?.workDays?.end ?? null
        }
      }
    };
    eSalary.eSalaryHbs.delivery = {
      sender: {
        heading: "SALAXY.REPORTS.eSalary.delivery.sender.heading",
        name: businessObject.employer?.avatar?.displayName,
        addressLine: businessObject.employer?.contact?.street,
        postalCode: businessObject.employer?.contact?.city,
        postOffice: businessObject.employer?.contact?.postalCode,
        countryCode: businessObject.employer?.contact?.countryCode,
        country: "Suomi - Finland",
        telephone: [
          {
            phoneNumber: businessObject.employer?.contact?.telephone
          }
        ],
        emailAddress: businessObject.employer?.contact?.email
      },
      recipient: {
        heading: "SALAXY.REPORTS.eSalary.delivery.recipient.heading",
        recipientName: {
          foreName: businessObject.worker.avatar?.firstName,
          surName: businessObject.worker.avatar?.lastName,
        },
        addressLine: businessObject.worker?.contact?.street,
        postalCode: businessObject.worker?.contact?.city,
        postOffice: businessObject.worker?.contact?.postalCode,
        countryCode: businessObject.worker?.contact?.countryCode,
        country: "Suomi - Finland",
      },
      payOffice: {
        fullName: "Suomen Palkanlaskenta Oy",
        addressLine: "Kirkkokatu 1 A",
        postalCode: "00170",
        postOffice: "Helsinki",
        countryCode: "FI",
        telephone: [
          { phoneNumber: "09 425 50603" }
        ],
        emailAddress: "aspa@palkkaus.fi"
      }
    };
    eSalary.eSalaryHbs.employee = {
      heading: "SALAXY.REPORTS.eSalary.employee.heading",
      identity: {
        label: "SALAXY.REPORTS.eSalary.employee.identity.label",
        item: businessObject.worker.paymentData.socialSecurityNumber
      },
      employmentDate: {
        label: "SALAXY.REPORTS.eSalary.employee.employmentDate.label",
        value: businessObject.worktime?.employmentPeriod?.start
      },
      classification: {
        heading: "SALAXY.REPORTS.eSalary.employee.classification.heading",
        jobTitle: {
          label: "SALAXY.REPORTS.eSalary.employee.classification.jobTitle.label",
          value: businessObject.info.workDescription
        }
      },
      employeeName: {
        foreName: businessObject.worker.paymentData.firstName,
        surName: businessObject.worker.paymentData.lastName
      }
    };

    eSalary.eSalaryHbs.bankAccount = {
      heading: "SALAXY.REPORTS.eSalary.bankAccount.heading",
      label: "SALAXY.REPORTS.eSalary.bankAccount.label",
      value: businessObject.worker.paymentData.ibanNumber,
      bic: BicHelper.getBicForIban(businessObject.worker.paymentData.ibanNumber)
    };

    eSalary.eSalaryHbs.calculationBases = {
      heading: "SALAXY.REPORTS.eSalary.calculationBases.heading",
      salaryRates: {
        heading: "SALAXY.REPORTS.eSalary.calculationBases.salaryRates.heading",
      },
      taxationRates: {
        heading: "SALAXY.REPORTS.eSalary.calculationBases.taxationRates.heading",
        recordLabels: {
          taxPercent: "SALAXY.REPORTS.eSalary.calculationBases.taxationRates.recordLabels.taxPercent",
          taxPercent2: "SALAXY.REPORTS.eSalary.calculationBases.taxationRates.recordLabels.taxPercent2"
        }
      },
      incomeLimitLabel: "SALAXY.REPORTS.eSalary.calculationBases.incomeLimitLabel",
      incomeLimit: businessObject.worker?.tax?.snapshot?.incomeLimit?.toString() ?? null,
      taxcardValidityLabel: "SALAXY.REPORTS.eSalary.calculationBases.taxcardValidityLabel",
      taxcardValidity: businessObject.worker?.tax?.snapshot?.validity?.start && businessObject.worker?.tax?.snapshot?.validity?.end ? `${Dates.format(businessObject.worker?.tax?.snapshot?.validity?.start)} - ${Dates.format(businessObject.worker?.tax?.snapshot?.validity?.end)}` : null
    };

    eSalary.eSalaryHbs.payDetails = {
      heading: "SALAXY.REPORTS.eSalary.payDetails.heading"
    };

    eSalary.eSalaryHbs.report = CalculationMapper.getCalculationReport(businessObject, ReportType.SalarySlip);
    this.addBenefitsToReportTotals(eSalary.eSalaryHbs.report);

    eSalary.eSalaryHbs.messages = {
      heading: "SALAXY.REPORTS.eSalary.messages.heading",
      salarySlipMessage: {
        label : "SALAXY.NG1.CalcDetailsWorkerComponent.salarySlipMessage",
        message: businessObject.info?.salarySlipMessage
      },
      transient: {
        heading: "SALAXY.REPORTS.eSalary.messages.transient.heading"
      }
    };

    eSalary.eSalaryHbs.cumulative = this.getCumulativeData(businessObject);
    return eSalary;
  }

  /**
   * get xml file name
   * @returns name of eSalary xml name
   */
  public static getXmlFileName(): string {
    return `PayslipXML`;
  }

  /**
   * Maps cumulative calculation to hbs supported format
   * @param calc cumulative calculation
   * @returns object containing array of current and previous earnings
   */
  private static getCumulativeData(calc: Calculation): IESalaryCumulative {
    const cumulativeDataDic = (calc as CumulativeCalculation).cumulative;
    if (cumulativeDataDic) {
      const cumulative = {
        heading: "SALAXY.REPORTS.eSalary.cumulative.heading",
        current: {
          heading: "SALAXY.REPORTS.eSalary.cumulative.current.heading",
          data: []
        },
        previous: {
          heading: "SALAXY.REPORTS.eSalary.cumulative.previous.heading",
          data: []
        }
      };
      const periodKeys = Object.keys(cumulativeDataDic);
      for (const periodKey of periodKeys) {
        const cumulativeData = cumulativeDataDic[periodKey];
        const dataKeys = Object.keys(cumulativeData.data);
        for (const dataKey of dataKeys) {
          if (periodKey === "current") {
            cumulative.current.data.push({label: cumulativeDataDic[periodKey].labels[dataKey], value: cumulativeDataDic[periodKey].data[dataKey]});
          } else {
            cumulative.previous.data.push({label: cumulativeDataDic[periodKey].labels[dataKey], value: cumulativeDataDic[periodKey][dataKey]});
          }
        }
      }
      return cumulative;
    } else {
      return null;
    }
  }

  private static addBenefitsToReportTotals(report: CalculationReport): void {
    let total = 0;
    const totals: any = report.rowGroups.find(x => (x as any).grouping === "totals");
    const benefits: any = report.rowGroups.find(x => (x as any).grouping === "benefits");
    if (benefits && benefits.rows.length) {
      benefits.rows.forEach(row => {
        total += row.data.irRow.total;
      });
      totals.rows.push({data: {irRow: {message: "SALAXY.REPORTS.ENUM.CalcGroup.benefits.label", total: total}}});
    }
  }
}